const PRICE_BUCKETS = [
  "selectedCamera",
  "selectedCabling",
  "selectedLabor",
  "selectedNVR",
  "selectedPoE",
  "selectedHardWare",
  "selectedSpecial",
];

const cleanText = (value) => String(value ?? "").trim().toLowerCase();
const toNumber = (value, fallback = 0) => {
  const n = Number(String(value ?? "").replace(/[$,]/g, "").trim());
  return Number.isFinite(n) ? n : fallback;
};

export const formatImportPriceMoney = (value) => `$${toNumber(value).toFixed(2)}`;

const getSku = (item = {}) => String(item.id || item.sku || item.Product_Name || item.Port_Name || item.NVR_Name || item.Product_SKU || "").trim();
const getQty = (item = {}) => toNumber(item.Product_Quantity ?? item.Camera_Quantity ?? item.Port_Quantity ?? item.quantity ?? 1, 1) || 1;
const getBase = (item = {}) => toNumber(
  item.Product_Base_Price ??
  item.Camera_Base_Price ??
  item.Port_Base_Price ??
  item.HardWare_Base_Price ??
  item.basePrice ??
  item.pricePerItem ??
  item.unitprice ??
  item.unit_price ??
  item.price ??
  item.price_2 ??
  0
);

const findProduct = (products = [], sku) => {
  const target = cleanText(sku);
  if (!target) return null;
  return products.find((p) => [p.id, p.sku, p.Product_Name, p.Product_SKU, p.productid].some((v) => cleanText(v) === target)) || null;
};

const currentBase = (product = {}, areDealer) => {
  const value = areDealer === "Dealer Selected"
    ? product.price_2 ?? product.saleprice ?? product.price ?? product.unitprice ?? product.unit_price
    : product.saleprice ?? product.price ?? product.unitprice ?? product.unit_price ?? product.price_2;
  return toNumber(value, NaN);
};

const optionRowsForProduct = (optionRows = [], sku) => {
  const target = cleanText(sku);
  const rows = [];
  let collecting = false;
  for (const row of optionRows) {
    const pid = cleanText(row?.productid);
    if (pid === target) {
      collecting = true;
      rows.push(row);
    } else if (collecting && !pid) {
      rows.push(row);
    } else if (collecting && pid) {
      break;
    }
  }
  return rows;
};

const currentOptionPrice = (optionRows, sku, name, value) => {
  const n = cleanText(name);
  const v = cleanText(value);
  if (!n || !v) return null;
  const match = optionRowsForProduct(optionRows, sku).find((row) => cleanText(row.featurecaption) === n && cleanText(row.featurename) === v);
  return match ? toNumber(match.featureprice, 0) : null;
};

const collectSelectedOptions = (item = {}) => {
  const options = [];
  const seen = new Set();
  for (let i = 1; i <= 12; i += 1) {
    const name = item[`option${i}_name`];
    const value = item[`option${i}_value`];
    if (!name || value === undefined || value === null || value === "") continue;
    const sig = `${cleanText(name)}::${cleanText(value)}`;
    if (seen.has(sig)) continue;
    seen.add(sig);
    options.push({
      key: `option${i}_price`,
      optionIndex: i,
      name,
      value,
      importedPrice: toNumber(item[`option${i}_price`], 0),
      aliasKey: `${name}_price`,
    });
  }

  Object.keys(item || {}).forEach((key) => {
    if (!key.endsWith("_price")) return;
    if (key === "unit_price") return;
    const name = key.slice(0, -6);
    if (!name || ["unit", "price", "Product_Base_Price", "Product_FinalPrice"].includes(name)) return;
    const value = item[name];
    if (value === undefined || value === null || value === "") return;
    const sig = `${cleanText(name)}::${cleanText(value)}`;
    if (seen.has(sig)) return;
    seen.add(sig);
    options.push({ key, name, value, importedPrice: toNumber(item[key], 0), aliasKey: key });
  });
  return options;
};

const optionUnitCost = (item = {}, optionRows = null, sku = "") => collectSelectedOptions(item).reduce((total, opt) => {
  if (Array.isArray(optionRows)) {
    const matched = currentOptionPrice(optionRows, sku, opt.name, opt.value);
    return total + (matched === null ? toNumber(opt.importedPrice) : toNumber(matched));
  }
  return total + toNumber(opt.importedPrice);
}, 0);

const optionBreakdownText = (options = [], priceField = "importedPrice") => {
  const priced = options.filter((opt) => Math.abs(toNumber(opt[priceField])) >= 0.005);
  if (!priced.length) return "Options: $0.00";
  return `Options: ${priced.map((opt) => `${opt.name}: ${opt.value} ${formatImportPriceMoney(opt[priceField])}`).join("; ")}`;
};

const getLineOptionLabel = (item = {}) => item.option ?? item.num_options ?? item.selectedOption ?? "";

const setTotals = (item, base, options) => {
  const qty = getQty(item);
  const unitTotal = toNumber(base) + toNumber(options);
  const lineTotal = unitTotal * qty;
  const updated = {
    ...item,
    Product_Base_Price: base,
    pricePerItem: base,
    unitprice: base,
    unit_price: base,
    unitPrice: unitTotal,
    totalPriceForItem: unitTotal,
    Product_FinalPrice: unitTotal,
    Product_Final_Price: lineTotal,
  };
  if (item.Camera_Base_Price !== undefined) updated.Camera_Base_Price = base;
  if (item.Camera_Final_Price !== undefined) updated.Camera_Final_Price = unitTotal;
  if (item.Port_Base_Price !== undefined) updated.Port_Base_Price = base;
  if (item.Port_Final_Price !== undefined) updated.Port_Final_Price = unitTotal;
  if (item.HardWare_Base_Price !== undefined) updated.HardWare_Base_Price = base;
  if (item.Hardware_New_Final_Price !== undefined) updated.Hardware_New_Final_Price = lineTotal;
  if (item.test !== undefined) updated.test = lineTotal;
  if (item.poeFinalPrice !== undefined) updated.poeFinalPrice = unitTotal;
  return updated;
};

const refreshItem = (item, products, optionRows, areDealer, bucket = "", index = 0) => {
  const sku = getSku(item);
  const product = findProduct(products, sku);
  if (!product) return { item, changes: [] };

  const importedBase = getBase(item);
  const csvBase = currentBase(product, areDealer);
  const currentBasePrice = Number.isFinite(csvBase) ? csvBase : importedBase;
  const optionComparisons = collectSelectedOptions(item).map((opt) => {
    const matched = currentOptionPrice(optionRows, sku, opt.name, opt.value);
    return { ...opt, currentPrice: matched === null ? opt.importedPrice : toNumber(matched, opt.importedPrice) };
  });
  const importedOptionTotal = optionComparisons.reduce((sum, opt) => sum + toNumber(opt.importedPrice), 0);
  const currentOptionTotal = optionComparisons.reduce((sum, opt) => sum + toNumber(opt.currentPrice), 0);

  let updated = { ...item };
  updated.Product_Base_Price = currentBasePrice;
  updated.pricePerItem = currentBasePrice;
  updated.unitprice = currentBasePrice;
  updated.unit_price = currentBasePrice;
  if (updated.Camera_Base_Price !== undefined) updated.Camera_Base_Price = currentBasePrice;
  if (updated.Port_Base_Price !== undefined) updated.Port_Base_Price = currentBasePrice;
  if (updated.HardWare_Base_Price !== undefined) updated.HardWare_Base_Price = currentBasePrice;

  optionComparisons.forEach((opt) => {
    updated[opt.key] = opt.currentPrice;
    if (opt.optionIndex) updated[`option${opt.optionIndex}_price`] = opt.currentPrice;
    if (opt.aliasKey) updated[opt.aliasKey] = opt.currentPrice;
  });
  updated = setTotals(updated, currentBasePrice, currentOptionTotal);

  const baseChanged = Math.abs(importedBase - currentBasePrice) >= 0.005;
  const optionsChanged = optionComparisons.some((opt) => Math.abs(toNumber(opt.importedPrice) - toNumber(opt.currentPrice)) >= 0.005);
  if (!baseChanged && !optionsChanged) return { item: updated, changes: [] };

  const importedUnit = importedBase + importedOptionTotal;
  const currentUnit = currentBasePrice + currentOptionTotal;
  return {
    item: updated,
    changes: [{
      bucket,
      index,
      option: getLineOptionLabel(updated),
      sku,
      kind: "line",
      type: "Base + selected options",
      from: importedUnit,
      to: currentUnit,
      qty: getQty(updated),
      importedBase,
      currentBase: currentBasePrice,
      importedOptionTotal,
      currentOptionTotal,
      importedUnit,
      currentUnit,
      optionComparisons,
      importedBreakdown: `Base: ${formatImportPriceMoney(importedBase)} | ${optionBreakdownText(optionComparisons, "importedPrice")}`,
      currentBreakdown: `Base: ${formatImportPriceMoney(currentBasePrice)} | ${optionBreakdownText(optionComparisons, "currentPrice")}`,
    }],
  };
};

export const refreshImportedCartPricing = (cartData = {}, products = [], optionRows = [], areDealer = "") => {
  const refreshed = { ...cartData };
  const changes = [];
  let totalChange = 0;

  PRICE_BUCKETS.forEach((bucket) => {
    const items = Array.isArray(cartData[bucket]) ? cartData[bucket] : [];
    refreshed[bucket] = items.map((item, index) => {
      const result = refreshItem(item, products, optionRows, areDealer, bucket, index);
      if (result.changes.length) {
        changes.push(...result.changes);
        totalChange += result.changes.reduce((sum, change) => sum + (toNumber(change.currentUnit) - toNumber(change.importedUnit)) * toNumber(change.qty ?? 1, 1), 0);
      }
      return result.item;
    });
  });

  return { cartData: refreshed, changes, totalChange };
};

const cloneCartData = (cartData = {}) => JSON.parse(JSON.stringify(cartData || {}));

const applyOldPriceChoice = (cartData, change) => {
  const item = cartData?.[change.bucket]?.[change.index];
  if (!item) return;
  if (change.kind === "line") {
    item.Product_Base_Price = change.importedBase;
    item.pricePerItem = change.importedBase;
    item.unitprice = change.importedBase;
    item.unit_price = change.importedBase;
    if (item.Camera_Base_Price !== undefined) item.Camera_Base_Price = change.importedBase;
    if (item.Port_Base_Price !== undefined) item.Port_Base_Price = change.importedBase;
    if (item.HardWare_Base_Price !== undefined) item.HardWare_Base_Price = change.importedBase;
    (change.optionComparisons || []).forEach((opt) => {
      item[opt.key] = opt.importedPrice;
      if (opt.optionIndex) item[`option${opt.optionIndex}_price`] = opt.importedPrice;
      if (opt.aliasKey) item[opt.aliasKey] = opt.importedPrice;
    });
    Object.assign(item, setTotals(item, change.importedBase, change.importedOptionTotal));
  }
};

const calcTotalDeltaFromChoices = (changes = [], selectedCurrent = {}) => changes.reduce((sum, change, idx) => {
  if (!selectedCurrent[idx]) return sum;
  return sum + (toNumber(change.currentUnit ?? change.to) - toNumber(change.importedUnit ?? change.from)) * toNumber(change.qty ?? 1, 1);
}, 0);

export const reviewImportPricingChanges = (pricingRefresh = {}) => {
  const changes = (pricingRefresh.changes || []).slice().sort((a, b) => {
    const optionA = toNumber(a.option, 9999);
    const optionB = toNumber(b.option, 9999);
    if (optionA !== optionB) return optionA - optionB;
    if ((a.bucket || "") !== (b.bucket || "")) return String(a.bucket || "").localeCompare(String(b.bucket || ""));
    return toNumber(a.index, 0) - toNumber(b.index, 0);
  });
  if (!changes.length || typeof document === "undefined") return Promise.resolve(pricingRefresh);
  return new Promise((resolve) => {
    const overlay = document.createElement("div");
    overlay.style.cssText = "position:fixed;inset:0;background:rgba(0,0,0,.45);z-index:99999;display:flex;align-items:center;justify-content:center;padding:24px;";
    const modal = document.createElement("div");
    modal.style.cssText = "background:#fff;border-radius:8px;width:min(1380px,98vw);max-height:88vh;display:flex;flex-direction:column;box-shadow:0 10px 40px rgba(0,0,0,.35);";
    modal.innerHTML = `<div style="padding:18px 22px;border-bottom:1px solid #ddd;"><h2 style="margin:0;font-size:24px;">Import Pricing Review</h2><div style="margin-top:6px;color:#555;">Pricing differences were found between the imported quote and the current pricing files. Choose which price to keep for each line.</div></div>`;
    const body = document.createElement("div");
    body.style.cssText = "overflow:auto;padding:16px 22px;";
    const table = document.createElement("table");
    table.style.cssText = "width:100%;border-collapse:collapse;";
    table.innerHTML = `<thead><tr><th style="text-align:left;border-bottom:1px solid #ddd;padding:8px;">SKU</th><th style="text-align:left;border-bottom:1px solid #ddd;padding:8px;">Price Field</th><th style="text-align:center;border-bottom:1px solid #ddd;padding:8px;">Imported Base / Options</th><th style="text-align:center;border-bottom:1px solid #ddd;padding:8px;">Current Base / Options</th><th style="text-align:right;border-bottom:1px solid #ddd;padding:8px;">Unit Change</th></tr></thead>`;
    const tbody = document.createElement("tbody");
    let lastOption = null;
    changes.forEach((change, idx) => {
      const optionLabel = change.option ? `Option ${change.option}` : "Option Unknown";
      if (optionLabel !== lastOption) {
        const groupTr = document.createElement("tr");
        groupTr.innerHTML = `<td colspan="5" style="padding:10px 8px;background:#f3f3f3;border-top:2px solid #ccc;border-bottom:1px solid #ddd;font-weight:700;">${optionLabel}</td>`;
        tbody.appendChild(groupTr);
        lastOption = optionLabel;
      }
      const tr = document.createElement("tr");
      const importedLabel = change.importedBreakdown || formatImportPriceMoney(change.from);
      const currentLabel = change.currentBreakdown || formatImportPriceMoney(change.to);
      const unitDelta = toNumber(change.currentUnit ?? change.to) - toNumber(change.importedUnit ?? change.from);
      tr.innerHTML = `<td style="padding:8px;border-bottom:1px solid #eee;">${change.sku || ""}</td><td style="padding:8px;border-bottom:1px solid #eee;">${change.type || ""}</td><td style="padding:8px;border-bottom:1px solid #eee;text-align:left;"><label style="display:flex;gap:8px;align-items:flex-start;"><input type="radio" name="price-choice-${idx}" value="old"><span>${importedLabel}<br><strong>Unit: ${formatImportPriceMoney(change.importedUnit ?? change.from)}</strong></span></label></td><td style="padding:8px;border-bottom:1px solid #eee;text-align:left;"><label style="display:flex;gap:8px;align-items:flex-start;"><input type="radio" name="price-choice-${idx}" value="new" checked><span>${currentLabel}<br><strong>Unit: ${formatImportPriceMoney(change.currentUnit ?? change.to)}</strong></span></label></td><td style="padding:8px;border-bottom:1px solid #eee;text-align:right;">${formatImportPriceMoney(unitDelta)}</td>`;
      tbody.appendChild(tr);
    });
    table.appendChild(tbody);
    body.appendChild(table);
    const footer = document.createElement("div");
    footer.style.cssText = "padding:14px 22px;border-top:1px solid #ddd;display:flex;align-items:center;justify-content:space-between;gap:12px;";
    const total = document.createElement("div");
    total.style.cssText = "font-weight:700;font-size:18px;";
    const updateTotal = () => {
      const selected = {};
      changes.forEach((_, idx) => { selected[idx] = modal.querySelector(`input[name=price-choice-${idx}][value=new]`)?.checked; });
      total.textContent = `Selected total change: ${formatImportPriceMoney(calcTotalDeltaFromChoices(changes, selected))}`;
    };
    modal.addEventListener("change", updateTotal);
    const buttons = document.createElement("div");
    buttons.style.cssText = "display:flex;gap:10px;";
    const keepImported = document.createElement("button");
    keepImported.textContent = "Keep All Imported";
    keepImported.style.cssText = "padding:10px 14px;";
    const useCurrent = document.createElement("button");
    useCurrent.textContent = "Use All Current";
    useCurrent.style.cssText = "padding:10px 14px;";
    const apply = document.createElement("button");
    apply.textContent = "Apply Selected";
    apply.style.cssText = "padding:10px 18px;background:#212529;color:#fff;border:0;border-radius:5px;";
    keepImported.onclick = () => { changes.forEach((_, idx) => { const input = modal.querySelector(`input[name=price-choice-${idx}][value=old]`); if (input) input.checked = true; }); updateTotal(); };
    useCurrent.onclick = () => { changes.forEach((_, idx) => { const input = modal.querySelector(`input[name=price-choice-${idx}][value=new]`); if (input) input.checked = true; }); updateTotal(); };
    apply.onclick = () => {
      const cartData = cloneCartData(pricingRefresh.cartData);
      const selected = {};
      changes.forEach((change, idx) => {
        const keepOld = modal.querySelector(`input[name=price-choice-${idx}][value=old]`)?.checked;
        selected[idx] = !keepOld;
        if (keepOld) applyOldPriceChoice(cartData, change);
      });
      document.body.removeChild(overlay);
      resolve({ ...pricingRefresh, cartData, totalChange: calcTotalDeltaFromChoices(changes, selected) });
    };
    buttons.appendChild(keepImported); buttons.appendChild(useCurrent); buttons.appendChild(apply);
    footer.appendChild(total); footer.appendChild(buttons);
    modal.appendChild(body); modal.appendChild(footer); overlay.appendChild(modal); document.body.appendChild(overlay); updateTotal();
  });
};

export const buildImportPricingChangeMessage = (changes = [], totalChange = 0) => {
  if (!changes.length) return "";
  return [
    "Pricing changes were found in the current pricing files.",
    "",
    ...changes.map((change) => `${change.sku} - ${change.type}: ${formatImportPriceMoney(change.importedUnit ?? change.from)} -> ${formatImportPriceMoney(change.currentUnit ?? change.to)}`),
    "",
    `Total quote change: ${formatImportPriceMoney(totalChange)}`,
  ].join("\n");
};
